import { Component, Input, OnInit } from '@angular/core';

import * as models from 'app/models';
import * as services from 'app/services';

// One strain's plants placed into one leaf zone.
interface Allocation {
  strainId: string;
  count: number;
}

// A leaf zone — the only level that actually holds plants. Capacity comes from
// the zone's own `placements`; parents are roll-ups and never allocated to.
interface LeafRow {
  id: string;
  name: string;
  capacity: number;
  ancestorIds: string[];   // every parent up the tree, persisted alongside the leaf
  assigned: boolean;
  allocations: Allocation[];
}

// Leaves sharing a parent path. Replaces the old N-column zoneTitles grid: the
// hierarchy becomes a section heading, which costs no horizontal space and works
// at any depth.
interface ZoneGroup {
  key: string;
  label: string;           // "Room 1" or "Room 1 › Bench A" once deeper than 2 levels
  leaves: LeafRow[];
}

type FillState = 'empty' | 'partial' | 'full' | 'over';

@Component({
  selector: 'app-zone-allocate',
  templateUrl: './zone-allocate.component.html',
  styleUrls: ['./zone-allocate.component.scss'],
  standalone: false
})
export class ZoneAllocateComponent implements OnInit {

  // The phase's strains, mutated in place on commit() — the dialog hands the
  // same HarvestPhase back to its caller, so counts and targets ride along.
  @Input() strains: Array<models.HarvestPhaseStrain> = [];

  zoneObject: models.DbZone;
  groups: Array<ZoneGroup> = [];
  loading: boolean = true;

  // Which strain the meters track and the fill actions spend from. Single-strain
  // phases never surface this — there is only ever one chip.
  activeStrainId: string;

  search: string = '';

  viewMode: 'list' | 'map' = 'list';
  floorPlans: Array<models.FloorPlan> = [];
  activeFloorPlan: models.FloorPlan;

  constructor(
    private zoneService: services.ZoneService,
    private floorPlanService: services.FloorPlanService,
  ) { }

  async ngOnInit() {
    this.activeStrainId = this.strains?.length ? this.strains[0].strainId : null;

    this.zoneObject = await this.zoneService.getZonesObject() || { zoneTitles: [], list: [] };
    this.build();
    this.refreshMapState();
    this.loading = false;

    // Map is offered only when the company has drawn a floor plan.
    this.floorPlans = await this.floorPlanService.getAll() || [];
    this.activeFloorPlan = this.floorPlans[0];
  }

  // --- model ----------------------------------------------------------------

  private build() {
    let groups = new Map<string, ZoneGroup>();
    this.walk(this.zoneObject.list || [], [], [], groups);
    this.groups = Array.from(groups.values());
  }

  private walk(
    list: Array<models.Zone>,
    ancestorIds: Array<string>,
    ancestorNames: Array<string>,
    groups: Map<string, ZoneGroup>
  ) {
    (list || []).forEach(zone => {
      let children = zone.zones || [];

      if (children.length > 0) {
        this.walk(children, [...ancestorIds, zone.id], [...ancestorNames, zone.name], groups);
        return;
      }

      // Leaf. Group it under its parent path; top-level leaves fall back to the
      // company's first zone-group title so the section is never nameless.
      let key = ancestorIds.length ? ancestorIds.join('/') : '__root__';
      let group = groups.get(key);
      if (!group) {
        group = {
          key,
          label: ancestorNames.length
            ? ancestorNames.join(' › ')
            : (this.zoneObject.zoneTitles?.[0] || 'Zones'),
          leaves: []
        };
        groups.set(key, group);
      }

      group.leaves.push({
        id: zone.id,
        name: zone.name,
        capacity: zone.placements || 0,
        ancestorIds: [...ancestorIds],
        assigned: this.isZoneAssigned(zone.id),
        allocations: this.hydrateAllocations(zone.id),
      });
    });
  }

  private isZoneAssigned(zoneId: string): boolean {
    return (this.strains || []).some(s => (s.zones || []).some(z => z.zoneId === zoneId));
  }

  // Existing saved allocations. Ancestor entries carry no count, so filtering on
  // count keeps roll-up rows from turning into phantom allocations.
  private hydrateAllocations(zoneId: string): Array<Allocation> {
    let out: Array<Allocation> = [];
    (this.strains || []).forEach(strain => {
      let saved = (strain.zones || []).find(z => z.zoneId === zoneId);
      if (saved && saved.count != null) {
        out.push({ strainId: strain.strainId, count: saved.count });
      }
    });
    return out;
  }

  // --- strain budget --------------------------------------------------------

  get multiStrain(): boolean {
    return (this.strains?.length || 0) > 1;
  }

  get activeStrain(): models.HarvestPhaseStrain {
    return (this.strains || []).find(s => s.strainId === this.activeStrainId);
  }

  setActiveStrain(strain: models.HarvestPhaseStrain) {
    this.activeStrainId = strain.strainId;
  }

  strainName(strainId: string): string {
    return (this.strains || []).find(s => s.strainId === strainId)?.strainName || 'Strain';
  }

  // Plants of this strain currently placed across every assigned zone.
  placedFor(strainId: string): number {
    let total = 0;
    this.groups.forEach(group => group.leaves.forEach(leaf => {
      if (!leaf.assigned) return;
      leaf.allocations.forEach(a => { if (a.strainId === strainId) total += (a.count || 0); });
    }));
    return total;
  }

  targetFor(strain: models.HarvestPhaseStrain): number {
    return strain?.count || 0;
  }

  // The phase target is entered upstream in the phase accordion and is very
  // often left blank. Editing it here saves a cancel / edit / reopen round trip
  // that would throw away the allocation in progress.
  //
  // Reads and writes the element directly rather than going through ngModel: a
  // clamped value that happens to equal the current model doesn't re-render with
  // one-way ngModel, which leaves the field showing a number we rejected.
  setTarget(strain: models.HarvestPhaseStrain, el: HTMLInputElement) {
    strain.count = this.readClamped(el, Number.MAX_SAFE_INTEGER);
    this.refreshMapState();
  }

  // Parses an input element, clamps to [0, max], and rewrites the field only if
  // clamping actually changed it — so typing is never interrupted mid-keystroke.
  private readClamped(el: HTMLInputElement, max: number): number {
    let raw = el.value;
    let v = Math.floor(Number(raw));
    if (raw === '' || isNaN(v) || v < 0) v = 0;
    if (v > max) v = max;
    if (raw !== '' && String(v) !== raw) el.value = String(v);
    return v;
  }

  remainingFor(strainId: string): number {
    let strain = (this.strains || []).find(s => s.strainId === strainId);
    return this.targetFor(strain) - this.placedFor(strainId);
  }

  // Meter width. With no target set there is no meaningful denominator, so the
  // bar stays empty rather than inventing a ratio.
  progressFor(strain: models.HarvestPhaseStrain): number {
    let target = this.targetFor(strain);
    if (target <= 0) return 0;
    return Math.min(100, (this.placedFor(strain.strainId) / target) * 100);
  }

  strainState(strain: models.HarvestPhaseStrain): 'none' | 'under' | 'exact' | 'over' {
    let target = this.targetFor(strain);
    let placed = this.placedFor(strain.strainId);
    if (target <= 0) return 'none';
    if (placed > target) return 'over';
    if (placed === target) return 'exact';
    return 'under';
  }

  // --- rows ----------------------------------------------------------------

  rowTotal(row: LeafRow): number {
    return row.allocations.reduce((sum, a) => sum + (a.count || 0), 0);
  }

  rowState(row: LeafRow): FillState {
    let total = this.rowTotal(row);
    if (total > row.capacity) return 'over';
    if (total === 0) return 'empty';
    if (total >= row.capacity) return 'full';
    return 'partial';
  }

  rowProgress(row: LeafRow): number {
    if (row.capacity <= 0) return 0;
    return Math.min(100, (this.rowTotal(row) / row.capacity) * 100);
  }

  toggleRow(row: LeafRow) {
    if (row.assigned) {
      row.assigned = false;
      row.allocations = [];
    } else {
      row.assigned = true;
      this.autoFill(row);
    }
    this.refreshMapState();
  }

  // Greedy fill for the active strain. Only ever writes into a zero — a number a
  // human typed is never overwritten, so toggling a room off and on again can't
  // silently destroy hand-tuned counts.
  private autoFill(row: LeafRow) {
    if (!this.activeStrainId) return;

    let alloc = row.allocations.find(a => a.strainId === this.activeStrainId);
    if (!alloc) {
      alloc = { strainId: this.activeStrainId, count: 0 };
      row.allocations.push(alloc);
    }
    if (alloc.count !== 0) return;

    let room = row.capacity - this.rowTotal(row);
    let remaining = Math.max(0, this.remainingFor(this.activeStrainId));
    alloc.count = Math.max(0, Math.min(room, remaining));
  }

  // Clamp to the zone's capacity, then squeeze the other strains in this zone so
  // the zone can never be oversubscribed.
  setCount(row: LeafRow, alloc: Allocation, el: HTMLInputElement) {
    alloc.count = this.readClamped(el, row.capacity);

    let room = row.capacity - alloc.count;
    row.allocations.forEach(other => {
      if (other === alloc) return;
      if (other.count > room) other.count = room;
      room -= other.count;
      if (room < 0) room = 0;
    });
    this.refreshMapState();
  }

  availableStrainsFor(row: LeafRow): Array<models.HarvestPhaseStrain> {
    let taken = row.allocations.map(a => a.strainId);
    return (this.strains || []).filter(s => !taken.includes(s.strainId));
  }

  // A row's strain picker offers only strains not already allocated here, plus
  // its own current value — two allocations of the same strain in one zone would
  // double-count against the budget.
  strainOptionsFor(row: LeafRow, alloc: Allocation): Array<models.HarvestPhaseStrain> {
    let taken = row.allocations.filter(a => a !== alloc).map(a => a.strainId);
    return (this.strains || []).filter(s => !taken.includes(s.strainId));
  }

  setAllocStrain(row: LeafRow, alloc: Allocation, strainId: string) {
    if (row.allocations.some(a => a !== alloc && a.strainId === strainId)) return;
    alloc.strainId = strainId;
    this.refreshMapState();
  }

  addAllocation(row: LeafRow) {
    let available = this.availableStrainsFor(row);
    if (!available.length) return;
    let room = Math.max(0, row.capacity - this.rowTotal(row));
    let remaining = Math.max(0, this.remainingFor(available[0].strainId));
    row.allocations.push({ strainId: available[0].strainId, count: Math.min(room, remaining) });
    this.refreshMapState();
  }

  removeAllocation(row: LeafRow, alloc: Allocation) {
    row.allocations = row.allocations.filter(a => a !== alloc);
    if (!row.allocations.length) row.assigned = false;
    this.refreshMapState();
  }

  // --- groups --------------------------------------------------------------

  groupCapacity(group: ZoneGroup): number {
    return group.leaves.reduce((sum, l) => sum + l.capacity, 0);
  }

  groupPlaced(group: ZoneGroup): number {
    return group.leaves.reduce((sum, l) => sum + (l.assigned ? this.rowTotal(l) : 0), 0);
  }

  groupAssignedCount(group: ZoneGroup): number {
    return group.leaves.filter(l => l.assigned).length;
  }

  groupAllAssigned(group: ZoneGroup): boolean {
    return group.leaves.length > 0 && group.leaves.every(l => l.assigned);
  }

  groupSomeAssigned(group: ZoneGroup): boolean {
    return group.leaves.some(l => l.assigned) && !this.groupAllAssigned(group);
  }

  toggleGroup(group: ZoneGroup) {
    if (this.groupAllAssigned(group)) {
      group.leaves.forEach(leaf => { leaf.assigned = false; leaf.allocations = []; });
    } else {
      group.leaves.forEach(leaf => {
        if (!leaf.assigned) {
          leaf.assigned = true;
          this.autoFill(leaf);
        }
      });
    }
    this.refreshMapState();
  }

  // Spend whatever is left of the active strain across zones that are already
  // assigned but still have room. Never reaches for new zones — expanding the
  // footprint stays an explicit choice.
  distributeRemaining() {
    if (!this.activeStrainId) return;
    this.groups.forEach(group => group.leaves.forEach(leaf => {
      if (!leaf.assigned) return;
      let remaining = this.remainingFor(this.activeStrainId);
      if (remaining <= 0) return;

      let room = leaf.capacity - this.rowTotal(leaf);
      if (room <= 0) return;

      let alloc = leaf.allocations.find(a => a.strainId === this.activeStrainId);
      if (!alloc) {
        alloc = { strainId: this.activeStrainId, count: 0 };
        leaf.allocations.push(alloc);
      }
      alloc.count += Math.min(room, remaining);
    }));
    this.refreshMapState();
  }

  clearAll() {
    this.groups.forEach(group => group.leaves.forEach(leaf => {
      leaf.assigned = false;
      leaf.allocations = [];
    }));
    this.refreshMapState();
  }

  // --- search ---------------------------------------------------------------

  get visibleGroups(): Array<ZoneGroup> {
    let term = (this.search || '').trim().toLowerCase();
    if (!term) return this.groups;

    return this.groups
      .map(group => {
        // A matching section name keeps all of its zones visible.
        if (group.label.toLowerCase().includes(term)) return group;
        let leaves = group.leaves.filter(l => l.name.toLowerCase().includes(term));
        return leaves.length ? { ...group, leaves } : null;
      })
      .filter(g => g != null);
  }

  get totalZonesAssigned(): number {
    return this.groups.reduce((sum, g) => sum + this.groupAssignedCount(g), 0);
  }

  get hasZones(): boolean {
    return this.groups.some(g => g.leaves.length > 0);
  }

  // --- map -----------------------------------------------------------------

  get canShowMap(): boolean {
    return this.floorPlans.length > 0;
  }

  get zonesList(): Array<models.Zone> {
    return this.zoneObject?.list || [];
  }

  setView(mode: 'list' | 'map') {
    this.viewMode = mode;
  }

  // Recomputed on mutation rather than exposed as getters: the map viewer rebuilds
  // its geometry whenever an input's identity changes, so fresh objects on every
  // change-detection pass would re-render the SVG continuously.
  mapSelectedIds: Array<string> = [];
  mapFillStates: { [zoneId: string]: FillState } = {};
  mapBadges: { [zoneId: string]: string } = {};

  // Assigned leaves plus their ancestors, so a room shape lights up when any of
  // its benches is in use. Parents summarise their subtree rather than showing
  // nothing, and assigned leaves carry a used/capacity badge.
  private refreshMapState() {
    let ids = new Set<string>();
    let states: { [zoneId: string]: FillState } = {};
    let badges: { [zoneId: string]: string } = {};

    this.groups.forEach(group => {
      let placed = 0, capacity = 0;

      group.leaves.forEach(leaf => {
        states[leaf.id] = leaf.assigned ? this.rowState(leaf) : 'empty';
        if (!leaf.assigned) return;

        ids.add(leaf.id);
        leaf.ancestorIds.forEach(id => ids.add(id));
        badges[leaf.id] = `${this.rowTotal(leaf)}/${leaf.capacity}`;
        placed += this.rowTotal(leaf);
        capacity += leaf.capacity;
      });

      let state: FillState = placed === 0 ? 'empty'
        : placed > capacity ? 'over'
        : placed >= capacity ? 'full' : 'partial';
      group.leaves.forEach(leaf => leaf.ancestorIds.forEach(id => states[id] = state));
    });

    this.mapSelectedIds = Array.from(ids);
    this.mapFillStates = states;
    this.mapBadges = badges;
  }

  onZoneTap(zoneId: string) {
    for (const group of this.groups) {
      // Tapping a room shape toggles the whole room, matching the list header.
      if (group.leaves.some(l => l.ancestorIds.includes(zoneId))) {
        this.toggleGroup(group);
        return;
      }
      let leaf = group.leaves.find(l => l.id === zoneId);
      if (leaf) {
        this.toggleRow(leaf);
        return;
      }
    }
  }

  // --- save ----------------------------------------------------------------

  // Writes the allocation back onto the phase's strains. Ancestor zones are
  // persisted without a count, which is how the rest of the app (zone
  // identifiers, harvest detail) resolves "which rooms is this phase in".
  commit() {
    (this.strains || []).forEach(strain => strain.zones = []);

    this.groups.forEach(group => group.leaves.forEach(leaf => {
      if (!leaf.assigned) return;

      leaf.allocations.forEach(alloc => {
        let strain = (this.strains || []).find(s => s.strainId === alloc.strainId);
        if (!strain) return;

        strain.zones.push({ zoneId: leaf.id, count: alloc.count || 0 });
        leaf.ancestorIds.forEach(ancestorId => {
          if (!strain.zones.some(z => z.zoneId === ancestorId)) {
            strain.zones.push({ zoneId: ancestorId });
          }
        });
      });
    }));
  }
}
